import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {makeModListControl} from '../../controls/mod-list-control/mod-list-control';
import {makeDesignModListDescriptor} from '../../controls/mod-list-control/mod-list-control-descriptors';
import {LinkWrapperControl} from '../../controls/link-wrapper-control/link-wrapper-control';
import {makeInfoBoxControl} from '../../controls/info-box-control/info-box-control';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {horizontalAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {borderRadiusDescriptor, opacityDescriptor, paddingDescriptor, responsiveWidthDescriptor} from '../../controls/slider-control/slider-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeCtaButtonControl} from '../../controls/cta-button-control/cta-button-control';
import {copyPasteStylesDescriptor} from '../../controls/cta-button-control/cta-button-control-descriptors';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {deleteControl, duplicateControl, lockControl} from '../shared/common-action-controls';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {WARNING_INFO_BOX_VARIANT_CLASS} from '../shared/sector-predicates';
import type {EmailMakerControl, EmailMakerSectorVisibilityCtx} from '../../right-panel-registry.types';

const stylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_styles');

const designModListControl: EmailMakerControl = makeModListControl(makeDesignModListDescriptor);
const urlControl: EmailMakerControl = LinkWrapperControl;
const copyPasteStylesControl = makeCtaButtonControl(copyPasteStylesDescriptor);

const designWarningControl = makeInfoBoxControl({
  contentKey: 'pmwjs_design_trimmed_warning',
  variantClass: WARNING_INFO_BOX_VARIANT_CLASS,
});

const horizontalAlignControl = makeOptionListControl(horizontalAlignDescriptor);

/** Shows only when the design src is a gif, which gets trimmed in the email. */
function isDesignWarningVisible({component}: EmailMakerSectorVisibilityCtx): boolean {
  const src = getStringProp(component, 'src');
  if (!src) {
    return false;
  }
  return /\.gif$/i.test(src.split('?')[0]);
}

const opacityControl = makeSliderControl(opacityDescriptor);
const widthControl = makeSliderControl(responsiveWidthDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);

registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN, {
  title: window.i18next.t('pmwjs_design'),
  properties: [
    {control: duplicateControl},
    {control: lockControl},
    {control: deleteControl},
    {control: designWarningControl, isVisible: isDesignWarningVisible},
    {control: designModListControl},
    {control: urlControl, actionMeta: {icon: 'icon-link', labelKey: 'pmwjs_link', complexity: TileComplexity.SIMPLE}},
    {control: stylesHeader},
    {control: copyPasteStylesControl},
    {control: opacityControl, tileMeta: {icon: 'icon-opacity', labelKey: 'pmwjs_opacity', complexity: TileComplexity.SIMPLE}},
    {control: widthControl, tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE}},
    {control: horizontalAlignControl, tileMeta: {icon: 'icon-h-align-center', labelKey: 'pmwjs_horizontal_alignment', complexity: TileComplexity.SIMPLE}},
    {control: borderStyleControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
    {control: borderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX}},
    {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
  ],
});
